import React, { useEffect, useState, useCallback } from "react";
import {
  Plus,
  Trash2,
  Lock,
  FileText,
  Users,
  ShieldCheck,
  X,
  ClockIcon,
  Edit2,
  Eye,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const PAGE_SIZE_OPTIONS = [4, 6, 10];

function Pagination({ total, page, pageSize, onPageChange, onPageSizeChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (safePage > 3) pages.push("…");
      for (
        let i = Math.max(2, safePage - 1);
        i <= Math.min(totalPages - 1, safePage + 1);
        i++
      )
        pages.push(i);
      if (safePage < totalPages - 2) pages.push("…");
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6">
      <div className="flex items-center gap-3 text-xs text-slate-500">
        <span>
          Showing{" "}
          <span className="font-medium text-slate-700 dark:text-slate-300">
            {start + 1}–{Math.min(start + pageSize, total)}
          </span>{" "}
          of{" "}
          <span className="font-medium text-slate-700 dark:text-slate-300">
            {total}
          </span>
        </span>
        <span className="hidden sm:inline text-slate-300 dark:text-slate-700">
          |
        </span>
        <div className="hidden sm:flex items-center gap-1.5">
          <span>Per page:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          >
            {PAGE_SIZE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(Math.max(1, safePage - 1))}
          disabled={safePage === 1}
          className="p-1.5 rounded-lg text-slate-500 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        {getPageNumbers().map((p, i) =>
          p === "…" ? (
            <span
              key={`e-${i}`}
              className="px-1 text-xs text-slate-400 select-none"
            >
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`min-w-[32px] h-8 rounded-lg text-xs font-medium transition ${safePage === p ? "bg-emerald-500 text-white shadow-sm" : "text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800"}`}
            >
              {p}
            </button>
          ),
        )}
        <button
          onClick={() => onPageChange(Math.min(totalPages, safePage + 1))}
          disabled={safePage === totalPages}
          className="p-1.5 rounded-lg text-slate-500 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function FamilyVault() {
  const [nominees, setNominees] = useState([]);
  const [showNomModal, setShowNomModal] = useState(false);
  const [editingNominee, setEditingNominee] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingNominee, setViewingNominee] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(4);

  const load = useCallback(async () => {
    try {
      const [n] = await Promise.all([api.get("/vault/inheritance")]);
      setNominees(n.data.family_members || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Reset page when switching tabs

  const handlePageSizeChange = (s) => {
    setPageSize(s);
    setPage(1);
  };

  const onDeleteNominee = async (familyMemberId) => {
    if (!window.confirm("Delete this nominee and all allocations?")) return;
    try {
      await api.delete(`/vault/inheritance/family-member/${familyMemberId}`);
      toast.success("Nominee deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const totalPages = Math.max(1, Math.ceil(nominees.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedNominees = nominees.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize,
  );

  return (
    <DashboardLayout>
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-600/10 text-emerald-600">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500">
              Continuity
            </p>
            <h1
              data-testid="vault-title"
              className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white"
            >
              Family Vault
            </h1>
          </div>
        </div>
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-400 max-w-2xl">
          A secure home for your nominees, encrypted documents, and emergency
          instructions — accessible to those you trust, when they need it most.
        </p>
      </div>

      <>
        <div className="flex justify-end mb-4">
          <button
            data-testid="vault-add-nominee"
            onClick={() => {
              setEditingNominee(null);
              setShowNomModal(true);
            }}
            className="btn-primary"
          >
            <Plus className="h-4 w-4" /> Add nominee
          </button>
        </div>

        {nominees.length === 0 ? (
          <Empty
            icon={Users}
            title="No inheritance allocations yet"
            desc="Assign assets to family members so they can access wealth distribution information."
          />
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-4">
              {pagedNominees.map((n) => (
                <div key={n.family_member_id} className="card-soft p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 min-w-0">
                      <div className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-full bg-gradient-to-br from-emerald-500 to-blue-600 text-white font-bold font-display text-lg">
                        {n.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-display text-lg font-bold text-slate-900 dark:text-white truncate">
                          {n.name}
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {n.relation}
                        </p>
                        {n.email && (
                          <p className="text-xs text-slate-500 mt-2 truncate">
                            {n.email}
                          </p>
                        )}
                        {n.phone && (
                          <p className="text-xs text-slate-500">{n.phone}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => {
                          setViewingNominee(n);
                          setShowViewModal(true);
                        }}
                        className="grid h-9 w-9 place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingNominee(n);
                          setShowNomModal(true);
                        }}
                        className="grid h-9 w-9 place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onDeleteNominee(n.family_member_id)}
                        className="grid h-9 w-9 place-items-center rounded-lg hover:bg-rose-50 dark:hover:bg-rose-500/10 text-rose-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-6">
                    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-4">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">
                        Assets Assigned
                      </div>
                      <div className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
                        {n.asset_count}
                      </div>
                    </div>
                    <div className="rounded-2xl border border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10 p-4">
                      <div className="text-[11px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                        Total Allocation
                      </div>
                      <div className="mt-2 text-2xl font-bold text-emerald-600">
                        ₹
                        {Number(n.total_assigned_value || 0).toLocaleString(
                          "en-IN",
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {nominees.length > pageSize && (
              <Pagination
                total={nominees.length}
                page={safePage}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </>
        )}
      </>

      {showNomModal && (
        <NomineeModal
          nominee={editingNominee}
          onClose={() => {
            setEditingNominee(null);
            setShowNomModal(false);
          }}
          onSaved={() => {
            setEditingNominee(null);
            setShowNomModal(false);
            load();
          }}
        />
      )}
      {showViewModal && viewingNominee && (
        <ViewNomineeModal
          nominee={viewingNominee}
          onClose={() => {
            setShowViewModal(false);
            setViewingNominee(null);
          }}
        />
      )}
    </DashboardLayout>
  );
}

function Empty({ icon: Icon, title, desc }) {
  return (
    <div className="card-soft p-12 text-center">
      <Icon className="h-10 w-10 text-slate-300 mx-auto" />
      <h3 className="mt-3 font-display font-bold">{title}</h3>
      <p className="mt-1 text-sm text-slate-500">{desc}</p>
    </div>
  );
}

// Each balance is either an asset or an insurance policy — build a stable key.
const optionId = (b) => `${b.kind || "asset"}:${b.asset_id || b.insurance_policy_id}`;

const makeAllocRow = () => ({ selection: "", quantity_value: "", notes: "" });

function NomineeModal({ nominee, onClose, onSaved }) {
  const isEdit = !!nominee;
  const [saving, setSaving] = useState(false);
  const [balances, setBalances] = useState([]);
  const [form, setForm] = useState({
    name: nominee?.name || "",
    relation: nominee?.relation || "",
    email: nominee?.email || "",
    phone: nominee?.phone || "",
  });
  // One or more allocation rows — each links a single asset/policy.
  const [rows, setRows] = useState([makeAllocRow()]);

  useEffect(() => {
    loadBalances();
  }, []);

  const loadBalances = async () => {
    try {
      const balancesRes = await api.get("/vault/inheritance/balances");
      setBalances(balancesRes.data.balances || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setRow = (i, patch) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const addRow = () => setRows((prev) => [...prev, makeAllocRow()]);

  const removeRow = (i) =>
    setRows((prev) => prev.filter((_, idx) => idx !== i));

  const balanceFor = (row) => balances.find((b) => optionId(b) === row.selection);

  // An option already chosen in another row can't be picked twice.
  const chosenElsewhere = (i) =>
    new Set(rows.filter((_, idx) => idx !== i).map((r) => r.selection));

  const availableCount = balances.length;
  const atRowCap = rows.length >= availableCount && availableCount > 0;

  const onSubmit = async (e) => {
    e.preventDefault();

    const allocations = [];
    for (const r of rows) {
      const bal = balanceFor(r);
      if (!bal) {
        toast.error("Select an asset or insurance policy for each allocation");
        return;
      }
      const qty = Number(r.quantity_value);
      if (!qty || qty <= 0) {
        toast.error(`Enter a valid amount for ${bal.asset_name}`);
        return;
      }
      if (qty > Number(bal.remaining_quantity) + 1e-9) {
        toast.error(
          `${bal.asset_name}: only ${bal.remaining_quantity} available`,
        );
        return;
      }
      allocations.push(
        bal.kind === "insurance"
          ? {
              insurance_policy_id: bal.insurance_policy_id,
              allocation_mode: "percentage",
              quantity_value: qty,
              notes: r.notes || undefined,
            }
          : {
              asset_id: bal.asset_id,
              allocation_mode: "quantity",
              quantity_value: qty,
              notes: r.notes || undefined,
            },
      );
    }

    setSaving(true);
    try {
      const payload = { allocations };
      if (isEdit) {
        payload.family_member_id = nominee.family_member_id;
      } else {
        payload.family_member = {
          name: form.name,
          relation: form.relation,
          email: form.email || undefined,
          phone: form.phone || undefined,
        };
      }
      await api.post("/vault/inheritance/batch", payload);
      const n = allocations.length;
      toast.success(
        isEdit
          ? `${n} allocation${n > 1 ? "s" : ""} added`
          : "Nominee created",
      );
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isEdit ? `Edit ${nominee.name}` : "Add inheritance allocation"}
      onClose={onClose}
    >
      <div className="max-h-[90vh] overflow-y-auto pr-2">
        <form onSubmit={onSubmit} className="space-y-4">
          {!isEdit && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Name" required>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="input-soft"
                  />
                </FormField>
                <FormField label="Relation" required>
                  <input
                    required
                    value={form.relation}
                    onChange={(e) =>
                      setForm({ ...form, relation: e.target.value })
                    }
                    className="input-soft"
                  />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Email">
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    className="input-soft"
                    placeholder="Optional"
                  />
                </FormField>
                <FormField label="Phone">
                  <input
                    value={form.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value })
                    }
                    className="input-soft"
                    placeholder="Optional"
                  />
                </FormField>
              </div>
              <div className="rounded-xl border border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10 p-4">
                <div className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                  Smart Linking Enabled
                </div>
                <div className="mt-1 text-xs text-emerald-700/80 dark:text-emerald-300/80">
                  If this email or phone number already belongs to a
                  AccessWealth user, the account will be linked automatically.
                </div>
              </div>
            </>
          )}
          {isEdit && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-900">
              <div className="font-semibold">{nominee.name}</div>
              <div className="text-xs text-slate-500 mt-1">
                {nominee.relation}
              </div>
              {nominee.email && (
                <div className="text-xs text-slate-500">{nominee.email}</div>
              )}
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-slate-500">
                Allocations
              </div>
              <div className="text-[11px] text-slate-400">
                {rows.length} of {availableCount || "—"} available
              </div>
            </div>

            {rows.map((row, i) => {
              const bal = balanceFor(row);
              const isInsurance = bal?.kind === "insurance";
              const taken = chosenElsewhere(i);
              return (
                <div
                  key={i}
                  className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] font-semibold text-slate-500">
                      Allocation {i + 1}
                    </div>
                    {rows.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRow(i)}
                        className="grid h-7 w-7 place-items-center rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>

                  <FormField label="Asset or Insurance" required>
                    <select
                      required
                      value={row.selection}
                      onChange={(e) =>
                        setRow(i, {
                          selection: e.target.value,
                          quantity_value: "",
                        })
                      }
                      className="input-soft"
                    >
                      <option value="">Select Asset or Insurance</option>
                      <optgroup label="Assets">
                        {balances
                          .filter((b) => (b.kind || "asset") === "asset")
                          .map((asset) => (
                            <option
                              key={optionId(asset)}
                              value={optionId(asset)}
                              disabled={taken.has(optionId(asset))}
                            >{`${asset.asset_name} • Remaining: ${asset.remaining_quantity} ${asset.quantity_label}`}</option>
                          ))}
                      </optgroup>
                      <optgroup label="Insurance">
                        {balances
                          .filter((b) => b.kind === "insurance")
                          .map((policy) => (
                            <option
                              key={optionId(policy)}
                              value={optionId(policy)}
                              disabled={taken.has(optionId(policy))}
                            >{`${policy.asset_name} • ${policy.remaining_quantity}% of cover available`}</option>
                          ))}
                      </optgroup>
                    </select>
                  </FormField>

                  {bal && (
                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 bg-slate-50 dark:bg-slate-900 text-xs">
                      <span className="text-slate-500">
                        {isInsurance ? "Sum Assured" : "Asset Value"}:{" "}
                      </span>
                      <span className="font-semibold">
                        ₹{Number(bal.asset_value).toLocaleString("en-IN")}
                      </span>
                      <span className="mx-2 text-slate-300">•</span>
                      <span className="text-slate-500">Remaining: </span>
                      <span className="font-semibold">
                        {bal.remaining_quantity}
                        {isInsurance ? "%" : ` ${bal.quantity_label}`}
                      </span>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      label={
                        isInsurance
                          ? "% of Sum Assured"
                          : bal
                            ? bal.quantity_label
                            : "Quantity"
                      }
                      required
                    >
                      <input
                        type="number"
                        step={isInsurance ? "0.01" : "0.0001"}
                        required
                        min="0"
                        max={bal?.remaining_quantity}
                        value={row.quantity_value}
                        onChange={(e) =>
                          setRow(i, { quantity_value: e.target.value })
                        }
                        className="input-soft"
                        placeholder={
                          bal
                            ? `Max ${bal.remaining_quantity}${isInsurance ? "%" : ""}`
                            : "Enter amount"
                        }
                      />
                    </FormField>
                    <FormField label="Notes">
                      <input
                        value={row.notes}
                        onChange={(e) => setRow(i, { notes: e.target.value })}
                        className="input-soft"
                        placeholder="Optional"
                      />
                    </FormField>
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              onClick={addRow}
              disabled={atRowCap}
              className="w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 py-3 text-sm font-medium text-slate-600 dark:text-slate-300 hover:border-emerald-400 hover:text-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <Plus className="h-4 w-4" />
              {atRowCap ? "All items allocated" : "Add another allocation"}
            </button>
          </div>

          <div className="flex gap-2 pt-2 sticky bottom-0 bg-white dark:bg-slate-950">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex-1"
            >
              {saving
                ? "Saving..."
                : isEdit
                  ? "Add Allocations"
                  : "Create"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-hidden card-soft flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-800">
          <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="grid h-8 w-8 place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </label>
      {children}
    </div>
  );
}

function ViewNomineeModal({ nominee, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(
        `/vault/inheritance/${nominee.family_member_id}`,
      );
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [nominee.family_member_id]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Modal title={nominee.name} onClose={onClose}>
      {loading ? (
        <div className="text-sm text-slate-500">Loading...</div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-5">
              <div className="text-xs uppercase tracking-wider text-slate-500">
                Assets Assigned
              </div>
              <div className="mt-2 text-3xl font-bold">
                {data.summary.asset_count}
              </div>
            </div>
            <div className="rounded-2xl border border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10 p-5">
              <div className="text-xs uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                Total Allocation
              </div>
              <div className="mt-2 text-3xl font-bold text-emerald-600">
                ₹
                {Number(data.summary.total_assigned_value).toLocaleString(
                  "en-IN",
                )}
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-4">
              Asset Allocations
            </div>
            <div className="space-y-3">
              {data.allocations.map((a) => (
                <div
                  key={a.allocation_id}
                  className="rounded-2xl border border-slate-200 dark:border-slate-700 p-5"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-semibold text-slate-900 dark:text-white">
                        {a.asset_name}
                      </div>
                      <div className="mt-1 text-xs text-slate-500 capitalize">
                        {a.asset_category}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-emerald-600">
                        ₹{Number(a.allocated_value).toLocaleString("en-IN")}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        Allocated Value
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-5">
                    <div>
                      <div className="text-[11px] text-slate-500">Assigned</div>
                      <div className="mt-1 font-semibold">
                        {a.assigned_quantity} {a.unit_type}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-slate-500">
                        Total Asset
                      </div>
                      <div className="mt-1 font-semibold">
                        {a.total_quantity} {a.unit_type}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-slate-500">
                        Ownership
                      </div>
                      <div className="mt-1 font-semibold">
                        {a.allocation_percentage}%
                      </div>
                    </div>
                  </div>
                  {a.notes && (
                    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 text-xs text-slate-500">
                      {a.notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
